import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { LinkedInFillDataRequest } from '../types/Dashboard';
import backendService from './backendservices/BackendService';

interface LinkedInStatsFormProps {
  open: boolean;
  onClose: () => void;
  weekStartDate: string;
  weekEndDate: string;
  onSuccess: () => void;
  onError?: (message: string) => void;
}

export default function LinkedInStatsForm({
  open,
  onClose,
  weekStartDate,
  weekEndDate,
  onSuccess,
  onError,
}: LinkedInStatsFormProps) {
  const [formData, setFormData] = useState<LinkedInFillDataRequest>({
    weekStartDate: weekStartDate,
    totalConnections: 0,
    connectionsSent: 0,
    connectionsAccepted: 0,
    invertedConnections: 0,
  });

  // Separate state for display values (strings)
  const [displayValues, setDisplayValues] = useState({
    totalConnections: '',
    connectionsSent: '',
    connectionsAccepted: '',
    invertedConnections: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);

  // Set active tab based on whether we have specific week data
  React.useEffect(() => {
    if (weekStartDate && weekEndDate) {
      setActiveTab(0); // Specific week
    } else {
      setActiveTab(1); // Current week
    }
  }, [weekStartDate, weekEndDate]);

  const handleInputChange =
    (field: keyof LinkedInFillDataRequest) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Update display value (string)
      setDisplayValues((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Update form data (number)
      const numValue = value === '' ? 0 : parseInt(value) || 0;
      setFormData((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    };

  const handleSubmit = async () => {
    // Basic validation
    if (
      formData.totalConnections < 0 ||
      formData.connectionsSent < 0 ||
      formData.connectionsAccepted < 0 ||
      formData.invertedConnections < 0
    ) {
      setError('All values must be non-negative numbers');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let response;

      if (activeTab === 0) {
        // Fill specific week data
        response = await backendService.Post(
          `${process.env.REACT_APP_BASE_URL}/linkedin-connection-stats/fill-week-data`,
          formData
        );
      } else {
        // Fill current week data
        const currentWeekData = {
          totalConnections: formData.totalConnections,
          connectionsSent: formData.connectionsSent,
          connectionsAccepted: formData.connectionsAccepted,
          invertedConnections: formData.invertedConnections,
        };
        response = await backendService.Post(
          `${process.env.REACT_APP_BASE_URL}/linkedin-connection-stats/weekly`,
          currentWeekData
        );
      }

      // Check if response is successful (status 200 or 201)
      if (response.status === 200 || response.status === 201) {
        console.log('Form submitted successfully:', response.data);

        // Reset form first
        setFormData({
          weekStartDate: weekStartDate,
          totalConnections: 0,
          connectionsSent: 0,
          connectionsAccepted: 0,
          invertedConnections: 0,
        });

        // Reset display values
        setDisplayValues({
          totalConnections: '',
          connectionsSent: '',
          connectionsAccepted: '',
          invertedConnections: '',
        });

        // Call success callback
        onSuccess();

        // Close the form
        onClose();
      } else {
        setError('Unexpected response from server');
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit data';
      setError(errorMessage);

      if (error.response?.status === 429 && onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start).toLocaleDateString();
    const endDate = new Date(end).toLocaleDateString();
    return `${startDate} - ${endDate}`;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Typography variant='h6'>Fill LinkedIn Connection Stats</Typography>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mt: 1 }}>
          <Tab label='Fill Specific Week' />
          <Tab label='Fill Current Week' />
        </Tabs>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeTab === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Week: {formatDateRange(weekStartDate, weekEndDate)}
          </Typography>
        )}

        {activeTab === 1 && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Current Week Data
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label='Total Connections'
            type='number'
            value={displayValues.totalConnections}
            onChange={handleInputChange('totalConnections')}
            fullWidth
            required
            helperText='Current total number of LinkedIn connections (cumulative)'
          />
          <TextField
            label='Connections Sent'
            type='number'
            value={displayValues.connectionsSent}
            onChange={handleInputChange('connectionsSent')}
            fullWidth
            required
            helperText='Number of connections sent this week'
          />
          <TextField
            label='Connections Accepted'
            type='number'
            value={displayValues.connectionsAccepted}
            onChange={handleInputChange('connectionsAccepted')}
            fullWidth
            required
            helperText='Number of connections accepted this week'
          />
          <TextField
            label='Inverted Connections'
            type='number'
            value={displayValues.invertedConnections}
            onChange={handleInputChange('invertedConnections')}
            fullWidth
            required
            helperText='Number of connections received this week'
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
